import React, { useState, useEffect, useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useLazyQuery } from '@apollo/client';
import { RMMClient } from '@nile/graphql';

import { DromoUploader, getTranslatedText, AppContext } from '@nile/components';
import { GET_ASSET_CLASS_V1_CUSTOM_FIELDS } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import Loader from '@nile/mangifera/src/Components/Ticket/TicketCount';
import {
  getFieldDescription,
  getFieldType,
  getSelectOptions,
  intlConverter
} from '../utils/helpers';
import { ImportIdentifier } from '../constants';
import {
  getAssetImportFields,
  getAssetValidators,
  getQuery,
  callQuery,
  shapeOptions
} from '../Fields/unmoniterAssetFields';

interface CustomField {
  label: string;
  columnName: string;
  fieldType?: string;
  options?: unknown[];
}

interface AssetClass {
  customFields: CustomField[];
}

interface FieldObj {
  type: string;
  key: string;
  selectOptions?: unknown[];
}


interface Props {
  classId: string;
  userToken?: string;
  activator?: React.ReactNode;
  onResults?: () => void;
  onCancel?: () => void;
}

const UnMonitorAssetUploader: React.FC<Props> = ({
  classId,
  userToken,
  activator,
  onResults,
  onCancel
}) => {
  const intl = useIntl();
  const appContext: any = useContext(AppContext);
  const currentProduct = appContext?.currentProduct || '';

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [fields, setFields] = useState<unknown[]>([]);

  const [
    getCustomFields,
    { loading: cfLoading, error: cfError }
  ] = useLazyQuery<{ getAssetClass: AssetClass }>(
    GET_ASSET_CLASS_V1_CUSTOM_FIELDS,
    {
      client: RMMClient,
      fetchPolicy: 'cache-and-network',
      variables: { classId },
      onCompleted({ getAssetClass }) {
        if (getAssetClass?.customFields) {
          const customFieldsToAdd = getAssetClass.customFields
            .map((cf: CustomField) => {
              const fieldDataObj = {
                label: cf.label,
                key: cf.columnName,
                type: getFieldType(cf),
                selectOptions: getSelectOptions(cf),
                validators: getAssetValidators(cf),
                description: getFieldDescription(intl, cf) || ''
              };
              return fieldDataObj;
            })
            .filter((cf) => cf?.key?.trim());

          setFields((prevFields) =>
            intlConverter(intl, [
              ...(prevFields as unknown[]),
              ...customFieldsToAdd
            ])
          );
        }
      }
    }
    );

  useEffect(() => {
    setLoading(true);
    const assetImportFields = getAssetImportFields(currentProduct);
    const initFields = [...assetImportFields];

    const selectFields = assetImportFields.reduce(
      (acc: Record<number, unknown>, field: unknown, idx: number) => {
        const fieldObj = field as FieldObj;
        if (fieldObj.type === 'select') {
          acc[idx] = getQuery(fieldObj.key)
            ? callQuery(fieldObj.key)
            : fieldObj.selectOptions;
        }
        return acc;
      },
      {}
    );
    const promises = Object.values(selectFields);
    Promise.all(promises)
      .then((options) => {
        options.forEach((set, idx) => {
          const indexToModify = Object.keys(selectFields)[idx];
          const fieldIndex = parseInt(indexToModify, 10);
          const fieldObj = initFields[fieldIndex] as FieldObj;
          fieldObj.selectOptions = shapeOptions(fieldObj.key, set);
        });
        setFields([...intlConverter(intl, initFields)]);
        if (classId) {
          getCustomFields();
        }
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setError(true);
        console.log(e);
      });
  }, [classId, currentProduct, getCustomFields, intl]);

  const customFieldValidatorRowHook = ({
    row,
    index
  }: {
    row: unknown;
    index: number;
  }) => {
    const newRecord = { row, index };

    // Basic validation can be added here if needed
    // For now, just return the record as is

    return newRecord;
  };

  if (loading || cfLoading) return <Loader loading />;

  if (error || cfError) return null;

  return (
    <DromoUploader
      settings={{
        importIdentifier: `${ImportIdentifier.UNMONITORED_ASSETS}:${classId}`,
        title: getTranslatedText(intl, 'common.import.asset')
      }}
      fields={fields}
      rowHooks={[(row: unknown) => row, customFieldValidatorRowHook]}
      columnHooks={[(column: unknown) => column]}
      stepHooks={[]}
      customUser={null}
      flags={{}}
      onResults={() => {
        if (onResults) onResults();
      }}
      onCancel={() => {
        if (onCancel) onCancel();
      }}
      activator={
        activator || (
          <div className="flex-center actionListItemPadding ptr">
            <div className="mr-10 flex center">
              <SVGIcon name="Assets" />
            </div>
            <FormattedMessage id="common.import.asset" />
          </div>
        )
      }
      userToken={userToken}
      backendSync
    />
  );
};

export default UnMonitorAssetUploader;
